<template>
  <div class="producer-products">
    <snow-data-table-header
      @add="() => $router.push({ name: 'product-new' })"
      addText="Novo Produto"
      :dates="dates"
      @searching="search"
      @open-filters="filter = true"
      @dates="updateDates"
      class="mb-3"
    />

    <div v-if="loading" class="centering pt-6">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
    </div>

    <template v-else-if="products.length">
      <div v-if="gallery" class="product-cards">
        <product-card
          v-for="product in products"
          :key="product.id"
          :product="product"
          @refresh="refresh"
        />
      </div>

      <div v-else class="product-table">
        <snow-data-table :data="products" :columns="columns">
          <template #links-de-divulgação="{ item }">
            <div class="d-flex">
              <snow-button variant="borderless" size="sm" @click="copy(item)">
                <i class="ph ph-link" />
              </snow-button>
              <snow-button
                variant="borderless"
                size="sm"
                @click="openLinks(item.id)"
              >
                <i class="ph ph-arrow-square-out" />
              </snow-button>
            </div>
          </template>
        </snow-data-table>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <snow-paginator
          v-model="page"
          :step="10"
          :pagination="pagination"
          @change-page="dispatch"
        />
      </div>
    </template>

    <div v-else class="d-flex justify-content-center w-100">
      <div class="no-content">
        <h2 class="text-center text-semibold">
          Ainda não há nada aqui
        </h2>
        <img
          class="d-block mx-auto mt-4"
          src="@/assets/images/no-content.svg"
        />
        <hr class="w-100" />
        <h2 class="mb-4">Talvez você precise</h2>
        <snow-button
          class="mb-4"
          icon="ph ph-plus"
          text="Cadastrar um produto"
          @click="
            () => {
              $router.push({ name: 'product-new' });
            }
          "
        />
        <h3 class="text-center fs-14 text-dark-40">
          Crie seu primeiro produto para começar suas vendas
        </h3>
      </div>
    </div>

    <responsive-modal
      v-model="filter"
      title="Filtrar"
      description="Selecione os filtros e aplique"
    >
      <product-filter
        :params="params"
        @submit="applyFilters"
        @close="filter = false"
      />
    </responsive-modal>
  </div>
</template>
<script>
import ProductFilter from "./Filter";
import ProductCard from "./ProductCard";
import { clearObject } from "@/functions/helpers";
import { mapState } from "vuex";
import moment from "moment";
import copy from "copy-text-to-clipboard";

export default {
  components: { ProductCard, ProductFilter },
  data() {
    return {
      loading: false,
      filter: false,
      page: 1,
      dates: [],
      params: {
        search: "",
        status: "",
        type_product: "",
        created_at_after: "",
        created_at_before: ""
      }
    };
  },
  computed: {
    ...mapState("product", ["products", "pagination"]),
    ...mapState("user", ["preferences"]),
    gallery() {
      return this.preferences.product_list === "gallery";
    },
    columns() {
      return [
        {
          name: "Produto",
          field: "name"
        },
        {
          name: "Tipo de produto",
          field: "type_product",
          transform: this.getProductType
        },
        {
          name: "Links de Divulgação",
          field: "oneclick_url",
          type: "link"
        },
        {
          name: "Data de criação",
          field: "created_at",
          transform: this.formatDate
        },
        {
          name: "Status",
          field: "status",
          transform: this.formatStatus,
          color: v =>
            ({
              active: "green",
              rejected: "negative",
              in_review: "alert",
              draft: "alert",
              inactive: "negative"
            }[v]),
          type: "tag"
        },
        {
          name: "",
          type: "menu",
          options: this.itemActions
        }
      ];
    }
  },
  methods: {
    applyFilters(data) {
      this.filter = false;
      this.params = {
        ...this.params,
        ...data
      };
      this.$store.commit("product/setProductFilters", this.params);
      this.refresh();
    },
    search(searchText) {
      this.params.search = searchText;
      this.dispatch();
    },
    refresh() {
      this.page = 1;
      this.dispatch();
    },
    dispatch(offset) {
      this.loading = true;

      this.$store
        .dispatch(
          "product/get",
          clearObject({ ...this.params, limit: 10, offset })
        )
        .catch(() => {
          this.$message.error("Erro ao recuperar produtos");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateDates(dates) {
      this.dates = dates;

      this.params.created_at_after = dates?.[0] || "";
      this.params.created_at_before = dates?.[1] || "";

      this.$store.commit("product/setFilterDates", dates);
      this.refresh();
    },
    getProductType(productType) {
      const options = {
        online_course: "Curso online",
        signature: "Assinatura",
        onsite_event: "Evento"
      };
      return options[productType] || productType;
    },
    formatDate(date) {
      if (!date) return "-";
      return (
        moment(date).format("DD/MM/YYYY") +
        " às " +
        moment(date).format("HH:mm")
      );
    },
    formatStatus(status) {
      const options = {
        draft: "Pendente",
        active: "Ativo",
        inactive: "Inativo",
        in_review: "Em revisão",
        reject: "Rejeitada"
      };
      return options[status] || status;
    },
    itemActions(product) {
      const actions = [];

      actions.push({
        label: "Copiar link do produto",
        click: this.copy
      });

      actions.push({
        label: "Duplicar produto",
        click: this.duplicate
      });

      if (product.status == "active") {
        actions.push({
          label: "Desativar produto",
          class: "text-danger",
          variant: "danger",
          dialog: {
            text:
              "Tem certeza que deseja desativar este produto? Ao confirmar este produto não estará mais disponível para compra até que você o reative.",
            variant: "negative",
            secondaryButtonText: "Cancelar",
            buttonText: "Desativar",
            icon: "fa fa-hexagon-exclamation",
            action: this.changeStatus
          }
        });
      } else {
        actions.push({
          label: "Ativar produto",
          click: this.changeStatus
        });
      }

      return actions;
    },
    copy(product) {
      copy(product.url);
      this.$message.success("Link copiado");
    },
    changeStatus(product) {
      this.submitStatus(
        product,
        product.status == "active" ? "inactive" : "active"
      );
    },
    submitStatus(item, status) {
      this.$store
        .dispatch("product/put", { id: item.id, status })
        .then(() => {
          this.$message.success(
            `O Produto foi ${status === "active" ? "ativado" : "desativado"}!`
          );
          this.refresh();
        })
        .catch(error => {
          this.errors = error.response.data;
          if (this.errors.non_field_errors) {
            this.$message.error(this.errors.non_field_errors[0]);
          }

          if (this.errors.details) {
            this.$message.error(this.errors.details);
          }

          if (error.response.data.status === "notfound") {
            this.$message.error("Sessão Expirada");
          }
        })
        .finally(() => {
          this.loadingPage = false;
        });
    },
    openLinks(product_id) {
      this.$router.push({
        name: "product-links",
        params: { id: product_id }
      });
    },
    duplicate(product) {
      this.$store.dispatch("product/duplicate", product.id).then(() => {
        this.$store.dispatch("product/get");
        this.$message.success("Produto duplicado!");
      });
    }
  },
  mounted() {
    const saved = this.$store.state.product.productFilters;
    if (saved) {
      this.dates = [
        saved.created_at_after || "",
        saved.created_at_before || ""
      ];
      this.params = { ...saved };
    }
    this.dispatch();
  }
};
</script>

<style lang="scss" scoped>
.product-cards {
  gap: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  transition: grid-template-columns 0.3s;
}
.search-component {
  max-width: 160px;

  @media screen and (max-width: 991px) {
    max-width: 100%;
  }
}

.calendar-picker {
  max-width: 230px;
  width: 100%;

  @media screen and (max-width: 991px) {
    max-width: 100%;
  }
}
.no-content {
  display: flex;
  width: 100%;
  margin-top: 100px;
  max-width: 380px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
</style>
